export interface Wap2026BlockItem {
  id?: number;
  blocks_advances_id?: number;
  component_id?: string;
  display_status?: number | string;
  jump_target?: unknown[];
  content?: Record<string, unknown>;
  [key: string]: unknown;
}

const CONTENT_COMPONENT_IDS = new Set([
  'swipeImage2026',
  'oneRowFourNav',
  'coupon',
  'regularList2026',
  'oneLineTwo2026',
  'imageTextGridNav',
  'waterFall2',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Block Init 会裁剪部分旧组件字段；仅为品牌图补齐完整接口中的背景色。
 */
export function supplement2026WapBrandImageBackground(
  initList: Wap2026BlockItem[],
  completeList: Wap2026BlockItem[],
): Wap2026BlockItem[] {
  return initList.map((block) => {
    if (
      block.component_id !== 'brandImage' ||
      block.bg_color != null ||
      block.blocks_advances_id == null
    ) {
      return block;
    }

    const fallbackColor = completeList.find(
      (item) => item.blocks_advances_id === block.blocks_advances_id,
    )?.bg_color;

    return typeof fallbackColor === 'string' && fallbackColor.trim()
      ? { ...block, bg_color: fallbackColor }
      : block;
  });
}

/**
 * 将 204 运行时接口的通用字段恢复为首页组件契约。
 */
export function normalizeBlockItems(list: Wap2026BlockItem[]): Wap2026BlockItem[] {
  return list.map((block) => {
    const normalized = { ...block };

    if (isRecord(normalized.jump_info)) {
      Object.assign(normalized, normalized.jump_info);
      delete normalized.jump_info;
    }

    if (
      normalized.layout_subtitl_color !== undefined &&
      normalized.layout_subtitle_color === undefined
    ) {
      normalized.layout_subtitle_color = normalized.layout_subtitl_color;
    }

    if (Array.isArray(normalized.items) && !normalized.jump_target) {
      normalized.jump_target = normalized.items.map((item) => {
        if (!isRecord(item)) {
          return item;
        }

        const normalizedItem = { ...item };

        if (normalizedItem.image !== undefined && normalizedItem.jump_image === undefined) {
          normalizedItem.jump_image = normalizedItem.image;
          delete normalizedItem.image;
        }

        if (isRecord(normalizedItem.jump_info)) {
          Object.assign(normalizedItem, normalizedItem.jump_info);
          delete normalizedItem.jump_info;
        }

        return normalizedItem;
      });
      delete normalized.items;
    }

    return normalized;
  });
}

/**
 * 204 正式和草稿接口当前都会把新版组件的 content 字段展开到模块顶层。
 * 在页面数据边界恢复保存协议中的 content 结构，组件内部只消费统一契约。
 */
export function normalize2026WapBlockItems<T extends Wap2026BlockItem>(
  list: T[],
): Array<T & { content?: Record<string, unknown> }> {
  return list.map((block) => {
    if (!block.component_id || !CONTENT_COMPONENT_IDS.has(block.component_id)) {
      return block;
    }

    if (block.component_id === 'coupon' && block.content) {
      return {
        ...block,
        content: {
          ...block.content,
          ...(block.display_type !== undefined ? { display_type: block.display_type } : {}),
          ...(block.coupon_list !== undefined ? { coupon_list: block.coupon_list } : {}),
        },
      };
    }

    if (block.content) return block;

    return {
      ...block,
      content: { ...block },
    };
  });
}

/**
 * 装修后台会校验浮动券唯一性；运行时仍兜底只保留后台顺序中的第一项。
 */
export function keepFirstFloatingCoupon<T extends Wap2026BlockItem>(list: T[]): T[] {
  let hasFloatingCoupon = false;

  return list.filter((block) => {
    if (block.component_id !== 'coupon') return true;

    const displayType = block.content?.display_type ?? block.display_type;
    if (Number(displayType) !== 2) return true;
    if (hasFloatingCoupon) return false;

    hasFloatingCoupon = true;
    return true;
  });
}

/**
 * 轮播图关闭“组件是否展示”后不应保留板位容器。
 */
export function removeHidden2026WapCarousels<T extends Wap2026BlockItem>(list: T[]): T[] {
  return list.filter((block) => {
    if (block.component_id !== 'swipeImage2026') return true;

    const displayStatus = block.content?.display_status;
    return Number(displayStatus) !== 2;
  });
}

/**
 * 204 正式页和 2026 预览页共用的数据归一化入口。
 */
export function normalize2026WapBlockInitData(list: Wap2026BlockItem[]): Wap2026BlockItem[] {
  const normalizedList = normalize2026WapBlockItems(normalizeBlockItems(list));

  return keepFirstFloatingCoupon(removeHidden2026WapCarousels(normalizedList));
}
